import { Box, Grid2 as Grid, ThemeProvider, Typography, createTheme, useMediaQuery } from '@mui/material';
import { COINS_API, USER_BY_HANDLENAME_API, USER_COUNTER_API } from '../../api/endpoint';
import { CoinsCounterProps, UserCounters, UserDetail } from '../../types';
import React, { useEffect, useState } from 'react';
import Latest from '../../components/latest';
import Loader from '../../components/loader';
import UserAvatarInfo from '../../components/user-avatar-info';
import UserBadges from '../../components/user-badges';
import UserScores from '../../components/user-scores';
import UserStats from '../../components/user-stats';
import axiosInstance from '../../api/axiosConfig';
import counterInstance from '../../api/counterConfig';
import createAxiosInstance from '../../api/axiosConfig';
import { useParams } from 'react-router-dom';
import user_profile_desktop from '../../assets/user_profile_desktop.png';
import user_profile_mobile from '../../assets/user_profile_mobile.png';

const theme = createTheme({
  typography: {
    fontFamily: 'Baloo 2'
  }
});

const parseCounter = (counter: { N: string } | undefined): number => parseInt(counter?.N || '0', 10);

const UserProfile: React.FC = () => {
  const { handle } = useParams<{ handle: string }>();
  const [userDetail, setUserDetail] = useState<UserDetail | null>(null);
  const [userCounters, setUserCounters] = useState<UserCounters | null>(null);
  const [coinsData, setCoinsData] = useState<CoinsCounterProps | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [userError, setUserError] = useState<string | null>(null);
  const [counterError, setCounterError] = useState<string | null>(null);

  const isMobile = useMediaQuery('(max-width:600px)');
  const searchTerm = `${handle}`;

  useEffect(() => {
    const fetchUserDetail = async () => {
      try {
        setLoading(true);
        const axiosInstance = createAxiosInstance(false);
        const userResponse = await axiosInstance.post(USER_BY_HANDLENAME_API, {
          searchTerm
        });

        const userData = userResponse.data?.data;

        if (userData) {
          setUserDetail(userData);
          await fetchUserCounters(userData.uid);
          await fetchCoins(userData.uid);
        } else {
          setUserError('User data not found.');
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        setUserError('Error fetching user details.');
      }
    };

    const fetchUserCounters = async (uid: any) => {
      try {
        const countersResponse = await counterInstance.get(`${USER_COUNTER_API}/${uid}`);
        const counters = countersResponse.data?.Item;

        if (counters) {
          const newCounters: Partial<UserCounters> = {};
          for (const key in counters) {
            if (counters[key].N) {
              newCounters[key as keyof UserCounters] = parseCounter(counters[key]);
            }
          }
          setUserCounters(newCounters as UserCounters);
        } else {
          setCounterError('User counters not found.');
        }
      } catch (error) {
        console.error('Error fetching user counters:', error);
        setCounterError('Error fetching user counters.');
      }
    };

    const fetchCoins = async (uid: string) => {
      try {
        const coinsResponse = await counterInstance.get(`${COINS_API}/${uid}`);
        const coinsItem = coinsResponse.data?.Item;

        if (coinsItem) {
          const newCoinsData: Partial<CoinsCounterProps> = {};
          for (const key in coinsItem) {
            if (coinsItem[key].N) {
              newCoinsData[key as keyof CoinsCounterProps] = parseCounter(coinsItem[key]);
            }
          }
          setCoinsData(newCoinsData as CoinsCounterProps);
        } else {
          console.error('Coins data not found.');
        }
      } catch (error) {
        console.error('Error fetching coin data:', error);
      }
    };

    const fetchData = async () => {
      try {
        await fetchUserDetail();
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [handle]);

  if (loading) return <Loader />;

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          flexGrow: 1,
          color: '#FFFFFF'
        }}>
        <Grid
          container
          spacing={1}
          justifyContent="space-between"
          sx={{
            pl: isMobile ? 2 : 8,
            pr: isMobile ? 2 : 8,
            pt: isMobile ? 2 : 8
          }}>
          {userError ? (
            <Typography color="error">{userError}</Typography>
          ) : (
            <UserAvatarInfo
              userDetail={userDetail}
              userCounters={userCounters}
              coinsData={coinsData}
              isMobile={isMobile}
            />
          )}
        </Grid>
        <Box
          sx={{
            pl: isMobile ? 2 : 8,
            pr: isMobile ? 2 : 8,
            pt: isMobile ? 2 : 8
          }}>
          <UserScores
            userCounters={userCounters}
            isMobile={isMobile}
            handle={handle}
            userId={userDetail?.uid}
            userDetail={userDetail}
          />
          <Box
            sx={{
              display: 'flex',
              flexGrow: '1',
              flexDirection: { xs: 'column', md: 'row' },
              color: '#FFFFFF',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: '0px', sm: '20px', md: '50px' }
            }}>
            <UserStats
              userCounters={userCounters}
              counterError={counterError}
              isMobile={isMobile}
              gradientTextStyles={gradientTextStyles}
            />
            <UserBadges
              userCounters={userCounters}
              counterError={counterError}
              isMobile={isMobile}
              gradientTextStyles={gradientTextStyles}
              userId={userDetail?.uid}
              userDetail={userDetail}
              handle={handle}
            />
          </Box>
        </Box>
        <Box
          sx={{
            pl: 1,
            pr: 1,
            pt: isMobile ? 2 : 8
          }}>
          <Latest isUserProfile={true} userId={userDetail?.uid} />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

// Styles function for gradient text
const gradientTextStyles = (isMobile: boolean) => ({
  background:
    'linear-gradient(89.82deg, #E02780 0.16%, #FA4E76 0.17%, #A784EA 38.04%, #ECA75C 62.96%, #F78D6D 83.4%, #CF66C6 99.85%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
  fontWeight: 400,
  letterSpacing: isMobile ? 1 : 3,
  mb: isMobile ? 2 : 5
});

export default UserProfile;
